/**
 * Social Share & Contact Components
 * Quick Win P0 features for lead generation
 */
import { Button } from '@/components/ui/button';
import { Phone, MessageCircle, Share2, QrCode, Download, Copy, Check } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';

/**
 * WhatsApp Contact Button
 * Opens WhatsApp with pre-filled message
 */
export function WhatsAppButton({ phone, message = '', className = '' }) {
  if (!phone) return null;
  
  // Clean phone number (remove spaces, dashes, etc.)
  const cleanPhone = phone.replace(/[\s\-\(\)]/g, '');
  // Add Poland country code if not present
  const fullPhone = cleanPhone.startsWith('+') ? cleanPhone : `+48${cleanPhone}`;
  
  const encodedMessage = encodeURIComponent(message);
  const whatsappUrl = `https://wa.me/${fullPhone.replace('+', '')}${message ? `?text=${encodedMessage}` : ''}`;
  
  return (
    <a
      href={whatsappUrl}
      target="_blank"
      rel="noopener noreferrer"
      className={className}
      data-testid="whatsapp-btn"
    >
      <Button 
        variant="outline" 
        className="bg-[#25D366]/10 border-[#25D366]/30 text-[#25D366] hover:bg-[#25D366]/20 gap-2"
      >
        <MessageCircle className="w-4 h-4" />
        WhatsApp
      </Button>
    </a>
  );
}

/**
 * Call Button (tel: link)
 */
export function CallButton({ phone, className = '' }) {
  if (!phone) return null;
  
  const cleanPhone = phone.replace(/[\s\-\(\)]/g, '');
  const telUrl = `tel:${cleanPhone.startsWith('+') ? cleanPhone : `+48${cleanPhone}`}`;
  
  return (
    <a href={telUrl} className={className} data-testid="call-btn">
      <Button 
        variant="outline" 
        className="bg-blue-500/10 border-blue-500/30 text-blue-400 hover:bg-blue-500/20 gap-2"
      >
        <Phone className="w-4 h-4" />
        Zadzwoń
      </Button>
    </a>
  );
}

/**
 * Facebook Share Button
 * Shares current page URL on Facebook
 */
export function FacebookShareButton({ url, title = '', className = '' }) {
  // Use current URL if not provided
  const shareUrl = url || (typeof window !== 'undefined' ? window.location.href : '');
  const encodedUrl = encodeURIComponent(shareUrl);
  const facebookShareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
  
  return (
    <a
      href={facebookShareUrl}
      target="_blank"
      rel="noopener noreferrer"
      className={className}
      data-testid="facebook-share-btn"
      onClick={(e) => {
        e.preventDefault();
        window.open(facebookShareUrl, 'facebook-share-dialog', 'width=626,height=436');
      }}
    >
      <Button 
        variant="outline" 
        className="bg-[#1877F2]/10 border-[#1877F2]/30 text-[#1877F2] hover:bg-[#1877F2]/20 gap-2"
      >
        <Share2 className="w-4 h-4" />
        Udostępnij
      </Button>
    </a>
  );
}

/**
 * Copy Link Button
 * Copies URL to clipboard
 */
export function CopyLinkButton({ url, className = '' }) {
  const [copied, setCopied] = useState(false);
  const shareUrl = url || (typeof window !== 'undefined' ? window.location.href : '');
  
  const handleCopy = async () => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(shareUrl);
      } else {
        const ta = document.createElement('textarea');
        ta.value = shareUrl;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopied(true);
      toast.success('Link skopiowany!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Nie udało się skopiować linku');
    }
  };
  
  return (
    <Button 
      variant="outline" 
      className={`bg-white/5 border-white/20 text-gray-300 hover:bg-white/10 gap-2 ${className}`}
      onClick={handleCopy}
      data-testid="copy-link-btn"
    >
      {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
      {copied ? 'Skopiowano!' : 'Kopiuj link'}
    </Button>
  );
}

/**
 * QR Code Component
 * Generates QR code for the page URL that can be downloaded
 */
export function QRCodeDisplay({ url, title = 'QR Code', size = 200 }) {
  const shareUrl = url || (typeof window !== 'undefined' ? window.location.href : '');
  const qrRef = useRef(null);
  
  const handleDownload = () => {
    try {
      // Get the SVG element and convert to PNG
      const svg = qrRef.current?.querySelector('svg');
      if (!svg) {
        toast.error('Nie udało się wygenerować QR code');
        return;
      }
      
      const svgData = new XMLSerializer().serializeToString(svg);
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        canvas.width = size;
        canvas.height = size;
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
        
        const pngUrl = canvas.toDataURL('image/png');
        const link = document.createElement('a');
        link.href = pngUrl;
        link.download = `${title.replace(/\s+/g, '_')}_qrcode.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success('QR code pobrany!');
      };
      
      img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
    } catch (err) {
      console.error('QR download error:', err);
      toast.error('Nie udało się pobrać QR code');
    }
  };
  
  return (
    <div className="flex flex-col items-center gap-4 p-4 bg-white rounded-xl" data-testid="qr-code-display" ref={qrRef}>
      <QRCodeSVG 
        value={shareUrl}
        size={size}
        level="M"
        includeMargin={true}
        bgColor="#ffffff"
        fgColor="#000000"
      />
      <div className="flex gap-2">
        <Button
          variant="default"
          size="sm"
          onClick={handleDownload}
          className="gap-2"
          data-testid="qr-download-btn"
        >
          <Download className="w-4 h-4" />
          Pobierz PNG
        </Button>
      </div>
    </div>
  );
}

/**
 * QR Code Modal Trigger
 * Shows QR code in a popover/modal
 */
export function QRCodeButton({ url, title = 'QR Code', className = '' }) {
  const [showQR, setShowQR] = useState(false);

  useEffect(() => {
    if (!showQR) return;
    const onKey = (e) => { if (e.key === 'Escape') setShowQR(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showQR]);
  
  return (
    <div className="relative">
      <Button 
        variant="outline" 
        className={`bg-white/5 border-white/20 text-gray-300 hover:bg-white/10 gap-2 ${className}`}
        onClick={() => setShowQR(!showQR)}
        data-testid="qr-code-btn"
      >
        <QrCode className="w-4 h-4" />
        Kod QR
      </Button>
      
      {showQR && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/50 z-40"
            onClick={() => setShowQR(false)}
          />
          
          {/* Modal */}
          <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50 animate-in fade-in zoom-in duration-200">
            <div className="bg-slate-900 border border-white/20 rounded-2xl p-6 shadow-2xl">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-white font-semibold">Kod QR</h3>
                <button 
                  onClick={() => setShowQR(false)}
                  className="text-zinc-400 hover:text-white"
                >
                  ×
                </button>
              </div>
              <QRCodeDisplay url={url} title={title} />
              <p className="text-zinc-400 text-sm text-center mt-4">
                Zeskanuj aby otworzyć stronę
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * WhatsApp SHARE (otwiera WhatsApp z linkiem do strony — inaczej niż WhatsAppButton, który dzwoni do numeru)
 */
export function WhatsAppShareButton({ url, text = '', className = '' }) {
  const shareUrl = url || (typeof window !== 'undefined' ? window.location.href : '');
  const href = `https://wa.me/?text=${encodeURIComponent(`${text ? text + ' ' : ''}${shareUrl}`)}`;
  return (
    <a
      href={href} target="_blank" rel="noopener noreferrer" className={className} data-testid="whatsapp-share-btn"
      onClick={(e) => { e.preventDefault(); window.open(href, '_blank', 'noopener'); }}
    >
      <Button variant="outline" className="bg-[#25D366]/10 border-[#25D366]/30 text-[#25D366] hover:bg-[#25D366]/20 gap-2">
        <MessageCircle className="w-4 h-4" /> WhatsApp
      </Button>
    </a>
  );
}

/**
 * X (Twitter) share
 */
export function XShareButton({ url, text = '', className = '' }) {
  const shareUrl = url || (typeof window !== 'undefined' ? window.location.href : '');
  const href = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`;
  return (
    <a
      href={href} target="_blank" rel="noopener noreferrer" className={className} data-testid="x-share-btn"
      onClick={(e) => { e.preventDefault(); window.open(href, 'x-share', 'width=560,height=480'); }}
    >
      <Button variant="outline" className="bg-white/5 border-white/20 text-gray-200 hover:bg-white/10 gap-2">
        <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor" aria-hidden="true"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
        X
      </Button>
    </a>
  );
}

/**
 * Native share (Web Share API) — na telefonie otwiera systemowy arkusz (Messenger, itd.); fallback: kopiuj link
 */
export function NativeShareButton({ url, title = '', text = '', className = '' }) {
  const shareUrl = url || (typeof window !== 'undefined' ? window.location.href : '');
  const handle = async () => {
    if (typeof navigator !== 'undefined' && navigator.share) {
      try { await navigator.share({ title, text, url: shareUrl }); } catch (e) { /* anulowano przez użytkownika */ }
    } else {
      try { await navigator.clipboard.writeText(shareUrl); toast.success('Link skopiowany!'); } catch (e) { toast.error('Nie udało się udostępnić'); }
    }
  };
  return (
    <Button
      variant="outline" onClick={handle} data-testid="native-share-btn"
      className={`bg-orange-500/10 border-orange-500/30 text-orange-300 hover:bg-orange-500/20 gap-2 ${className}`}
    >
      <Share2 className="w-4 h-4" /> Więcej
    </Button>
  );
}

/**
 * PageShareBar — UDOSTĘPNIANIE STRONY (nie kontakt): Facebook, WhatsApp, X, kopiuj link, QR + natywny share.
 */
export function PageShareBar({ url, title = '', text = '', showQR = true, className = '' }) {
  return (
    <div className={`flex flex-wrap gap-2 ${className}`} data-testid="page-share-bar">
      <FacebookShareButton url={url} title={title} />
      <WhatsAppShareButton url={url} text={text} />
      <XShareButton url={url} text={text} />
      <CopyLinkButton url={url} />
      {showQR && <QRCodeButton url={url} title={title} />}
      <NativeShareButton url={url} title={title} text={text} />
    </div>
  );
}

/**
 * Social Share Bar
 * Combined component with all sharing options
 */
export function SocialShareBar({ 
  phone, 
  url, 
  title = '',
  whatsappMessage = '',
  showWhatsApp = true,
  showCall = true,
  showFacebook = true,
  showCopyLink = true,
  showQR = true,
  className = ''
}) {
  return (
    <div className={`flex flex-wrap gap-2 ${className}`} data-testid="social-share-bar">
      {showWhatsApp && phone && (
        <WhatsAppButton phone={phone} message={whatsappMessage} />
      )}
      {showCall && phone && (
        <CallButton phone={phone} />
      )}
      {showFacebook && (
        <FacebookShareButton url={url} title={title} />
      )}
      {showCopyLink && (
        <CopyLinkButton url={url} />
      )}
      {showQR && (
        <QRCodeButton url={url} title={title} />
      )}
    </div>
  );
}

export default SocialShareBar;
